import FetchBeneficiaryTypeGrants from '@Application/SubjectTypeDefinitions/Queries/FetchBeneficiaryTypeGrants';
import fetchBeneficiaryTypeGrants from '@Application/SubjectTypeDefinitions/fetchBeneficiaryTypeGrants';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import GridCard from '@Guidelines/Component/GridCard';
import SubjectTypeTile from '@Guidelines/Component/SubjectTypeTile';
import { theme } from '@Guidelines/Theme/theme';
import describeAccess from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/SubjectTypeCard/Helpers/describeAccess';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Tag } from '@verclam/ui-kit';

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const accessStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

const actionsStyle = css`
	display: flex;
	justify-content: flex-end;
`;

/**
 * The system beneficiary type: only who may use it is administrable, so the card has no archive menu.
 * A failed read leaves the access sentence out; the access page surfaces the failure itself.
 */
const BeneficiaryTypeCard: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const { data: grants } = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.beneficiaryTypeGrants,
		stopOn: (value) => accountId === null || (value !== null && value.accountId === accountId),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchBeneficiaryTypeGrants(new FetchBeneficiaryTypeGrants(accountId)));
		},
		deps: [accountId],
	});

	const handleEditClick = React.useCallback(() => {
		navigate('/administration/subject-types/beneficiary');
	}, [navigate]);

	return (
		<GridCard layout="stacked" rowGap="0.75rem">
			<div css={titleRowStyle}>
				<SubjectTypeTile icon="person" color="cyan" />
				<h2 css={titleStyle}>Bénéficiaires</h2>
				<Tag value="Type système" severity="secondary" rounded />
			</div>
			<Eyebrow>Fiche et circuits gérés par le système</Eyebrow>
			<ConditionalLoader data={grants}>
				{(present) => <p css={accessStyle}>{describeAccess(present.usableBy)}</p>}
			</ConditionalLoader>
			<div css={actionsStyle}>
				<Button
					type="button"
					text
					icon={<Pencil size={16} />}
					label="Modifier l'accès"
					onClick={handleEditClick}
				/>
			</div>
		</GridCard>
	);
};

export default BeneficiaryTypeCard;
